import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { Textarea } from '@journ/ui/textarea'
import { useEffect, useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { updateWorkflow } from '../application/manage-workflow'
import { activeWorkflow$ } from '../store'
import { workflows as workflowsPort, notify } from '../wiring'
import { useWorkflowEditorParams } from './use-workflow-editor-params'

type Props = {
	onSuccess: () => void
}

const WorkflowInfoEditSheet = ({ onSuccess }: Props) => {
	const { editWorkflowInfo, setParams } = useWorkflowEditorParams()
	const workflow = useSelector(activeWorkflow$)
	const content = useIntlayer('workflow-editor').editWorkflow

	const isOpen = Boolean(editWorkflowInfo) && workflow !== null

	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [submitting, setSubmitting] = useState(false)

	useEffect(() => {
		if (isOpen && workflow) {
			setName(workflow.name)
			setDescription(workflow.description ?? '')
		}
	}, [isOpen, workflow])

	const close = () => setParams({ editWorkflowInfo: null })

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!workflow || !name.trim()) return
		setSubmitting(true)
		const updated = await updateWorkflow(
			workflowsPort,
			{ id: workflow.id, name: name.trim(), description: description.trim() || null },
			notify,
		)
		setSubmitting(false)
		if (updated) {
			close()
			onSuccess()
		}
	}

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={content.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{content.title}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>

				<form onSubmit={handleSubmit} className='flex flex-col gap-5'>
					<div className='flex flex-col gap-2'>
						<Label htmlFor='wf-name'>{content.nameLabel}</Label>
						<Input
							id='wf-name'
							value={name}
							onChange={e => setName(e.target.value)}
							placeholder={content.namePlaceholder.value}
							autoFocus
						/>
					</div>

					<div className='flex flex-col gap-2'>
						<Label htmlFor='wf-desc'>{content.descriptionLabel}</Label>
						<Textarea
							id='wf-desc'
							value={description}
							onChange={e => setDescription(e.target.value)}
							placeholder={content.descriptionPlaceholder.value}
							rows={3}
							className='resize-none'
						/>
					</div>

					<div className='flex justify-end gap-2 pt-2'>
						<Button type='button' variant='outline' onClick={close} disabled={submitting}>
							{content.cancel}
						</Button>
						<Button type='submit' disabled={submitting || !name.trim()}>
							{content.save}
						</Button>
					</div>
				</form>
			</SheetContent>
		</Sheet>
	)
}

export { WorkflowInfoEditSheet }
